import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import { strictFetcher } from './strict-fetcher'

export type ChartRange = '1d' | '7d' | '30d' | 'all'

interface ChartPoint {
	height: number
	ts: number
	reserve0: string
	reserve1: string
}

interface ChartCandle {
	height: number
	ts: number
	/// OHLC of the reserve-ratio price reserve1/reserve0 — re-orient per
	/// market with 1/x (swapping high/low) when the base is token1.
	open: number
	high: number
	low: number
	close: number
}

export interface PoolChartResponse {
	pool_id: string
	tip: number
	points?: ChartPoint[]
	candles?: ChartCandle[]
	bucket_blocks?: number
	/// True while the server is still backfilling this (pool, range) in the
	/// background — the series is partial, so derived stats must not be shown.
	building?: boolean
}

/// Historical reserve series for one pool. The server backfills points via
/// simulate-at-height and caches them permanently, so the first request for a
/// (pool, range) can take a few seconds — after that it's instant. Candle
/// mode sub-samples 4x finer, so its first build takes proportionally longer.
export default function usePoolChart(
	poolId: string | null,
	range: ChartRange,
	candles = false
): {
	data: PoolChartResponse | undefined
	isLoading: boolean
	error: unknown
	retry: () => void
} {
	const { data, isLoading, error, mutate } = useSwr<PoolChartResponse>(
		poolId
			? `${API_URL}/alkane/pool/${poolId}/chart?range=${range}${candles ? '&candles=true' : ''}`
			: null,
		strictFetcher,
		{ refreshInterval: 60_000, revalidateOnFocus: false, keepPreviousData: true }
	)
	return { data, isLoading, error, retry: () => void mutate() }
}
